import { useCallback, useMemo, useState } from 'react';
import type { ColumnsType, ColumnType } from 'antd/es/table';

import { resolveColumnKey } from '../utils/resolveColumnKey';

export interface IColumnVisibilityState {
  [columnKey: string]: boolean;
}

export const useColumnVisibility = <T extends object>(
  columns: ColumnType<T>[],
  onColumnVisibilityChange?: (visibilityState: IColumnVisibilityState) => void
) => {
  const initialState = useMemo(() => {
    const state: IColumnVisibilityState = {};
    columns.forEach(col => {
      const key = resolveColumnKey(col);
      if (key) {
        const defaultVisible = (col as any).defaultVisible ?? true;
        state[key] = defaultVisible;
      }
    });
    return state;
  }, [columns]);

  const [visibilityState, setVisibilityState] =
    useState<IColumnVisibilityState>(initialState);

  const setVisibility = useCallback(
    (key: string, visible: boolean) => {
      setVisibilityState(prev => {
        const newState = {
          ...prev,
          [key]: visible,
        };
        onColumnVisibilityChange?.(newState);
        return newState;
      });
    },
    [onColumnVisibilityChange]
  );

  const processedColumns = useMemo(() => {
    return columns.filter(col => {
      const key = resolveColumnKey(col);
      return key ? visibilityState[key] !== false : true;
    }) as ColumnsType<T>;
  }, [columns, visibilityState]);

  return {
    visibilityState,
    setVisibility,
    processedColumns,
  };
};
